import React from 'react';

// This draft is scoped to what Croop's code actually does today (see
// BIG_PICTURE.md) — it is not legal advice. Confirm data-processing
// regions and any additional legal-notice (Impressum) requirements
// before relying on this as a complete privacy notice.
const DATA_CONTROLLER_NAME = 'Cristina Melnic';
const DATA_CONTROLLER_ADDRESS = 'Moritzstrasse 7, 10969 Berlin, Germany';
const PRIVACY_CONTACT_EMAIL = 'cristina.v.melnic@gmail.com';
const LAST_UPDATED = 'September 2026';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="croop-privacy-page">
      <div className="croop-privacy-panel">
        <a href="/" className="croop-route-brand" aria-label="Croop">
          <img className="croop-route-logo" src="/croop-horizontal.svg" alt="" />
        </a>

        <h1 className="croop-privacy-title">Privacy Policy</h1>
        <p className="croop-privacy-updated">Last updated: {LAST_UPDATED}</p>

        <section className="croop-privacy-section">
          <h2>1. Who we are</h2>
          <p>
            Croop is a dance-practice app that lets you split choreography videos into
            practiceable segments. The data controller responsible for your data is:
          </p>
          <p>
            {DATA_CONTROLLER_NAME}
            <br />
            {DATA_CONTROLLER_ADDRESS}
            <br />
            Contact: {PRIVACY_CONTACT_EMAIL}
          </p>
        </section>

        <section className="croop-privacy-section">
          <h2>2. What data we collect</h2>
          <ul>
            <li>
              <strong>Account data:</strong> email address, display name, and (if you sign in
              with Google) profile photo URL. Passwords are handled entirely by Firebase
              Authentication — we never see or store your password.
            </li>
            <li>
              <strong>Videos and practice data:</strong> if you upload a video and save it to
              your library, the video file, its generated thumbnail, and your segments/settings
              (loop points, speed, mirror state, titles) are stored against your account.
            </li>
            <li>
              <strong>YouTube practice:</strong> when you practice on a YouTube video, playback
              happens directly in an embedded YouTube player — we do not download, store, or
              rehost that video. If you're signed in and save the session, we store only the
              YouTube video ID, your chosen start time, and your segments/settings, subject to{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                Google's Privacy Policy
              </a>.
            </li>
            <li>
              <strong>Sharing:</strong> if you create a share link for a video, we store the
              share's view/expiry limits and access counters. We don't collect personal data
              about people who view a shared link.
            </li>
            <li>
              <strong>Payments:</strong> if you buy extra storage, checkout is handled entirely
              by Stripe on Stripe's own hosted page — we never see or store your card details.
              We keep a record of the purchase (package, amount, timestamp) for your account and
              for accounting purposes.
            </li>
            <li>
              <strong>Usage counters:</strong> library item counts and limits, so we can enforce
              free-tier limits and reflect your purchased storage.
            </li>
          </ul>
          <p>
            We do not use analytics or advertising cookies. The only browser storage we use is
            Firebase Authentication's own session storage, which exists solely to keep you
            signed in and is strictly necessary for the app to function.
          </p>
        </section>

        <section className="croop-privacy-section">
          <h2>3. Why we process it, and on what legal basis</h2>
          <ul>
            <li>To provide the core service (uploading, segmenting, and practicing videos) — performance of a contract with you.</li>
            <li>To process payments for extra storage — performance of a contract, and our legitimate interest in preventing fraud.</li>
            <li>To enforce free-tier limits and keep the service running reliably — legitimate interest.</li>
            <li>Where you've given it, your consent (e.g. agreeing to this policy when creating an account).</li>
          </ul>
        </section>

        <section className="croop-privacy-section">
          <h2>4. Who we share data with</h2>
          <p>We use a small number of processors to run Croop:</p>
          <ul>
            <li><strong>Firebase (Google Cloud)</strong> — authentication, database, file storage, and backend functions.</li>
            <li><strong>Stripe</strong> — payment processing for storage purchases.</li>
            <li><strong>YouTube (Google)</strong> — embedded video playback when you practice on a YouTube video.</li>
          </ul>
          <p>
            These providers may process data outside your country, including outside the
            EU/EEA. Where that happens, we rely on the safeguards each provider offers (such as
            Standard Contractual Clauses). We do not sell your data or share it with advertisers.
          </p>
        </section>

        <section className="croop-privacy-section">
          <h2>5. How long we keep your data</h2>
          <p>
            We keep your account and library data for as long as your account exists. If you
            delete your account, we delete your uploaded videos, thumbnails, library/playlist
            data, and any shares you own straightaway. Records of past purchases may be kept
            separately for as long as required by tax and accounting law, even after account
            deletion.
          </p>
        </section>

        <section className="croop-privacy-section">
          <h2>6. Your rights</h2>
          <p>Under GDPR, you have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you.</li>
            <li>Correct inaccurate data (e.g. update your display name in-app).</li>
            <li>
              Erase your data — you can permanently delete your account and all associated data
              at any time from the account menu (<strong>Delete my account</strong>), or by
              emailing us at {PRIVACY_CONTACT_EMAIL}.
            </li>
            <li>Request a copy of your data in a portable format.</li>
            <li>Object to or restrict certain processing.</li>
            <li>Lodge a complaint with your local data protection supervisory authority.</li>
          </ul>
        </section>

        <section className="croop-privacy-section">
          <h2>7. Children</h2>
          <p>Croop is not directed at children under 16, and we do not knowingly collect data from them.</p>
        </section>

        <section className="croop-privacy-section">
          <h2>8. Changes to this policy</h2>
          <p>
            We may update this policy as the app changes. We'll update the "last updated" date
            above when we do.
          </p>
        </section>

        <section className="croop-privacy-section">
          <h2>9. Contact</h2>
          <p>Questions about this policy or your data? Email us at {PRIVACY_CONTACT_EMAIL}.</p>
        </section>

        <a href="/" className="croop-privacy-back">Back to Croop</a>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
